import React from 'react'
import { eventsDefinitions } from 'shared/store/metadata'
import { Chevron, Minus, Plus } from 'app/assets/icons'
import styled from 'styled-components'
import { media } from 'app/assets/styles'
import { Link } from 'app/common/link'
import { NavigationItemIcon } from 'app/common/navigation-item-icon'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'
import type { MenuItemDTO } from 'shared/types'
import { CcCartCheck } from 'app/ecommerce/click-and-collect/cart-check'
import { colors } from 'shared/assets/styles'
import { styleH6 } from 'app/common/typography'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'
import { featureCodes } from 'shared/utils'

const { grey30, veryLightGrey, text } = colors

type Props = {
    index: number,
    item: MenuItemDTO,
    close: () => void,
    selectedTab: ?number,
    setSelectedTab: number => void,
    intl: IntlShape
}
const NavigationItemPrev = (props: Props) => {
    const { close, index, item, selectedTab, setSelectedTab, intl } = props
    const { actionUrl, label, external, items: subTabs, subLabel } = item

    if (!actionUrl && subTabs.length === 0) {
        return null
    }

    const icon = <StyledNavigationItemIcon color={text} {...item} />
    const path = actionUrl && actionUrl.split('/')[1]
    const metaData = {
        context: {
            type: 'navigation',
            path
        }
    }
    const ga4Meta = {
        ...eventsDefinitions.GA4_USER_NAVIGATION,
        access_type: 'menu',
        cta_name: label
    }

    const CLICK_AND_COLLECT = intl.formatMessage({
        id: 'routes.clickandcollect.restaurant.choice'
    })

    const chevron = (
        <AnimatedChevron>
            <Chevron color={text} />
        </AnimatedChevron>
    )
    const plus = (
        <PlusContainer>
            <Plus color={text} />
        </PlusContainer>
    )

    const minus = (
        <MinusContainer>
            <Minus color={text} />
        </MinusContainer>
    )

    const renderSubTabs = (subTabs: Array<MenuItemDTO>) => {
        return subTabs.map((item, index) => {
            const { label, actionUrl, external } = item
            if (!actionUrl || !label) {
                return null
            }
            const isFaq = actionUrl === '/service-conso'
            const isActiveZendesk = useSelector((state: RootState) =>
                state.featuresState.available.includes(
                    featureCodes.ACTIVATION_ZENDESK_REDIRECTION
                )
            )
            const faqRoute = process.env.REACT_APP_ZENDESK_URL ?? ''
            return (
                <SubTabsContainer>
                    {renderSection(
                        label.toUpperCase(),
                        isFaq && !!isActiveZendesk ? faqRoute : actionUrl,
                        isFaq && !!isActiveZendesk ? isFaq : external
                    )}
                </SubTabsContainer>
            )
        })
    }

    const renderTab = () => (
        <div>
            <Line>
                {icon}
                <Label>{!!label && label.toUpperCase()}</Label>
                {chevron}
            </Line>
            {!!subLabel && <SubLine>{subLabel}</SubLine>}
        </div>
    )

    const renderSection = (
        section: any,
        actionUrl: string,
        external?: boolean
    ) => {
        return actionUrl === CLICK_AND_COLLECT ? (
            <CcCartCheck>{section}</CcCartCheck>
        ) : (
            <Link
                onClick={close}
                url={actionUrl}
                external={external}
                meta={[metaData, ga4Meta]}>
                {section}
            </Link>
        )
    }

    return (
        <Item key={index}>
            {subTabs.length === 0 ? (
                <Wrapper>
                    {actionUrl &&
                        renderSection(renderTab(), actionUrl, external)}
                </Wrapper>
            ) : (
                <div>
                    <StyledMainTab
                        status={selectedTab !== index}
                        onClick={() => setSelectedTab(index)}>
                        <Line>
                            {icon}
                            <Label>{!!label && label.toUpperCase()}</Label>
                            {selectedTab === index ? minus : plus}
                        </Line>
                        {!!subLabel && <SubLine>{subLabel}</SubLine>}
                    </StyledMainTab>
                    {!!subTabs &&
                        selectedTab === index &&
                        renderSubTabs(subTabs)}
                </div>
            )}
        </Item>
    )
}

const Item = styled.li`
    ${styleH6}
    padding: 0 40px;
    font-family: 'Roboto Condensed';
    font-weight: 300;
    white-space: nowrap;
    ${media.medium`
        padding: 0 60px;
    `};
`

const Label = styled.span``

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 10px 0;
    border-bottom: solid 1px ${grey30};
    font-style: Roboto Condensed;
    ${media.medium`
        padding: 30px 0;
    `};
`

const StyledMainTab = styled.div`
    padding: 10px 0;
    border-bottom: ${props => props.status && `solid 1px ${grey30}`};
    ${media.medium`
        border-bottom: solid 1px ${grey30};
        padding: 30px 0;
    `};
`

const SubTabsContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: auto;
    margin: 0 -40px;
    background-color: ${veryLightGrey};
    padding: 5px 40px;
    ${media.medium`
        margin: 0;
        padding: 10px 30px;
        font-size: 18px;
        border-bottom: solid 1px ${grey30};
    `};
`

const AnimatedChevron = styled.div`
    display: none;
    transition: all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
    ${media.small`
        display: block;
        margin-left: auto;

        ${Wrapper}:hover & {
            transform: translate(-50%, 0);
        }

        ${Wrapper}:active & {
            transform: none;
        }
     `};
`

const PlusContainer = styled.div`
    display: block;
    margin-left: auto;
    ${media.small`
        display: block;
        margin-left: auto;

     `};
`

const MinusContainer = styled.div`
    display: block;
    margin-left: auto;
    ${media.small`
        display: block;
        margin-left: auto;
     `};
`

const Line = styled.span`
    display: flex;
    align-items: center;
    justify-content: flex-start;
`

const SubLine = styled(Line)`
    font-size: 12px;
    line-height: 18px;
    text-transform: none;
    padding-top: 5px;
    ${media.medium`
        display: none;
    `};
`

const StyledNavigationItemIcon = styled(NavigationItemIcon)`
    margin-right: 10px;
`

const NavigationItem = injectIntl(NavigationItemPrev)
export { NavigationItem }
